import dayjs from "dayjs"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import {
  formatDisplayName,
  getUserFullName,
} from "../../helpers/userDisplay"

export const PAGE_SIZE = 10

export const MODULE_META = {
  member_profile: { label: "Member Profile", tone: "navy" },
  inbox_post: { label: "Inbox Post", tone: "blue" },
  messages: { label: "Messages", tone: "orange" },
  project: { label: "Project", tone: "green" },
  photography: { label: "Photography", tone: "red" },
}

export const STATUS_META = {
  under_review: { label: "Under Review", tone: "review", tab: "under_review" },
  resolved: { label: "Resolved", tone: "resolved", tab: "resolved" },
  dismissed: { label: "Dismissed", tone: "dismissed", tab: "closed" },
  closed: { label: "Closed", tone: "closed", tab: "closed" },
  withdrawn: { label: "Withdrawn", tone: "withdrawn", tab: "withdrawn" },
}

export const TAB_KEYS = [
  "all",
  "under_review",
  "resolved",
  "withdrawn",
]

export const TAB_LABELS = {
  all: "All",
  under_review: "Under Review",
  resolved: "Resolved",
  closed: "Closed",
  withdrawn: "Withdrawn",
}

export const MODULE_FILTER_OPTIONS = Object.entries(MODULE_META).map(
  ([value, meta]) => ({
    value,
    label: meta.label,
  }),
)

export const STATUS_FILTER_OPTIONS = [
  { value: "opened", label: "Opened" },
  { value: "under_review", label: "Under Review" },
  { value: "resolved", label: "Resolved" },
  { value: "dismissed", label: "Dismissed" },
  { value: "withdrawn", label: "Withdrawn" },
]

export const TAB_TO_API_STATUS = {
  under_review: "opened",
  resolved: "resolved",
  closed: "closed",
  withdrawn: "withdrawn",
}

const DEFAULT_THREAD = [
  {
    id: "msg-1",
    author: "Jake Rivera",
    role: "City Editor",
    isYou: false,
    createdAt: dayjs().subtract(2, "hour").toISOString(),
    body: "Thank you for bringing this to our attention. We have reviewed the initial information and will investigate further.",
  },
  {
    id: "msg-2",
    author: "Jerry Micheal",
    role: "You",
    isYou: true,
    createdAt: dayjs().subtract(1, "hour").toISOString(),
    body: "Thank you for the update. Please let me know if you need more screenshots or context.",
  },
  {
    id: "msg-3",
    author: "Jake Rivera",
    role: "City Editor",
    isYou: false,
    createdAt: dayjs().subtract(2, "hour").toISOString(),
    body: "We have spoken with the member and are continuing our review. We appreciate your patience.",
  },
]

export const DUMMY_FLAGS = [
  {
    id: "dummy-1",
    code: "FLG-20482",
    userId: "user-alex",
    name: "Alex Rivera",
    city: "Dallas, TX",
    moduleKey: "member_profile",
    reason: "Inappropriate behavior",
    description:
      "Received inappropriate and offensive messages about my work. The messages made me feel uncomfortable and unsafe collaborating with this member.",
    status: "under_review",
    submittedAt: "2026-05-12T10:42:00",
    evidence: [{ id: "ev-1", name: "screenshot2303r324", size: "1.8 MB", type: "IMG" }],
    messages: DEFAULT_THREAD,
  },
  {
    id: "dummy-2",
    code: "FLG-20481",
    userId: "user-jordan",
    name: "Jordan Chen",
    city: "Austin, TX",
    moduleKey: "inbox_post",
    reason: "Spam or misleading content",
    description: "This inbox post looks like spam promoting an unrelated service.",
    status: "resolved",
    submittedAt: "2026-05-15T09:18:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-3",
    code: "FLG-20480",
    userId: "user-sam",
    name: "Sam Taylor",
    city: "Dallas, TX",
    moduleKey: "messages",
    reason: "Inappropriate messaging",
    description: "Received inappropriate messages in a project thread.",
    status: "under_review",
    submittedAt: "2026-05-14T16:05:00",
    evidence: [{ id: "ev-2", name: "message-capture", size: "420 KB", type: "IMG" }],
    messages: DEFAULT_THREAD,
  },
  {
    id: "dummy-4",
    code: "FLG-20479",
    userId: "user-emily",
    name: "Emily Johnson",
    city: "Houston, TX",
    moduleKey: "inbox_post",
    reason: "Hate speech or discrimination",
    description: "The post contains discriminatory language toward other members.",
    status: "dismissed",
    submittedAt: "2026-05-13T11:22:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-5",
    code: "FLG-20478",
    userId: "user-alex",
    name: "Alex Rivera",
    city: "Dallas, TX",
    moduleKey: "member_profile",
    reason: "False reporting or abuse of flagging system",
    description: "This profile appears to be abusing the flagging system.",
    status: "resolved",
    submittedAt: "2026-05-12T10:42:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-6",
    code: "FLG-20477",
    userId: "user-chris",
    name: "Chris Martinez",
    city: "San Antonio, TX",
    moduleKey: "project",
    reason: "Copyright or intellectual property violation",
    description: "Project assets appear to use copyrighted material without credit.",
    status: "under_review",
    submittedAt: "2026-05-11T13:40:00",
    evidence: [{ id: "ev-3", name: "project-asset", size: "2.1 MB", type: "IMG" }],
    messages: DEFAULT_THREAD,
  },
  {
    id: "dummy-7",
    code: "FLG-20476",
    userId: "user-morgan",
    name: "Morgan Lee",
    city: "Dallas, TX",
    moduleKey: "member_profile",
    reason: "Impersonation or identity fraud",
    description: "This profile appears to impersonate another member.",
    status: "resolved",
    submittedAt: "2026-05-09T08:15:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-8",
    code: "FLG-20475",
    userId: "user-casey",
    name: "Casey Brooks",
    city: "Fort Worth, TX",
    moduleKey: "messages",
    reason: "Threatening or violent behavior",
    description: "Threatening language was used in direct messages.",
    status: "under_review",
    submittedAt: "2026-05-08T19:02:00",
    evidence: [],
    messages: DEFAULT_THREAD,
  },
  {
    id: "dummy-9",
    code: "FLG-20474",
    userId: "user-taylor",
    name: "Taylor Swift",
    city: "Austin, TX",
    moduleKey: "inbox_post",
    reason: "Spam or misleading content",
    description: "Repeated promotional posts in the city inbox.",
    status: "dismissed",
    submittedAt: "2026-05-07T12:30:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-10",
    code: "FLG-20473",
    userId: "user-taylor",
    name: "Taylor Swift",
    city: "Austin, TX",
    moduleKey: "inbox_post",
    reason: "Spam or misleading content",
    description: "Duplicate spam content in the city inbox.",
    status: "dismissed",
    submittedAt: "2026-05-07T09:05:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-11",
    code: "FLG-20472",
    userId: "user-dana",
    name: "Dana Wilson",
    city: "Dallas, TX",
    moduleKey: "photography",
    reason: "Inappropriate content in uploads",
    description: "Uploaded photography contains inappropriate imagery.",
    status: "under_review",
    submittedAt: "2026-05-06T15:48:00",
    evidence: [{ id: "ev-4", name: "upload-preview", size: "3.4 MB", type: "IMG" }],
    messages: [],
  },
  {
    id: "dummy-12",
    code: "FLG-20471",
    userId: "user-riley",
    name: "Riley Park",
    city: "Houston, TX",
    moduleKey: "member_profile",
    reason: "Harassment or inappropriate behavior",
    description: "Repeated harassment on the public profile.",
    status: "resolved",
    submittedAt: "2026-05-05T17:10:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-13",
    code: "FLG-20470",
    userId: "user-jamie",
    name: "Jamie Foster",
    city: "Dallas, TX",
    moduleKey: "messages",
    reason: "Privacy violation - sharing personal info",
    description: "Personal contact details were shared without consent.",
    status: "under_review",
    submittedAt: "2026-05-04T11:26:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-14",
    code: "FLG-20469",
    userId: "user-quinn",
    name: "Quinn Adams",
    city: "Austin, TX",
    moduleKey: "project",
    reason: "Unauthorized commercial activity",
    description: "Project is being used to sell services outside the platform.",
    status: "dismissed",
    submittedAt: "2026-05-03T10:00:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-15",
    code: "FLG-20468",
    userId: "user-noah",
    name: "Noah Bennett",
    city: "Dallas, TX",
    moduleKey: "photography",
    reason: "Copyright or intellectual property violation",
    description: "Photos appear to belong to another artist.",
    status: "withdrawn",
    submittedAt: "2026-05-02T14:20:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-16",
    code: "FLG-20467",
    userId: "user-maya",
    name: "Maya Thompson",
    city: "Austin, TX",
    moduleKey: "project",
    reason: "Spam or misleading content",
    description: "Project description is misleading about collaboration terms.",
    status: "withdrawn",
    submittedAt: "2026-05-01T09:45:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-17",
    code: "FLG-20466",
    userId: "user-priya",
    name: "Priya Sharma",
    city: "Houston, TX",
    moduleKey: "messages",
    reason: "Inappropriate messaging",
    description: "Unsolicited messages after leaving a project.",
    status: "closed",
    submittedAt: "2026-04-30T18:12:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-18",
    code: "FLG-20465",
    userId: "user-james",
    name: "James Okonkwo",
    city: "Dallas, TX",
    moduleKey: "member_profile",
    reason: "Impersonation or identity fraud",
    description: "Profile photo and bio match another known artist.",
    status: "closed",
    submittedAt: "2026-04-29T08:33:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-19",
    code: "FLG-20464",
    userId: "user-elena",
    name: "Elena Rossi",
    city: "Austin, TX",
    moduleKey: "inbox_post",
    reason: "Hate speech or discrimination",
    description: "Inbox post includes discriminatory comments.",
    status: "withdrawn",
    submittedAt: "2026-04-28T13:55:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-20",
    code: "FLG-20463",
    userId: "user-daniel",
    name: "Daniel Brooks",
    city: "Fort Worth, TX",
    moduleKey: "project",
    reason: "Threatening or violent behavior",
    description: "Project comments include threatening language.",
    status: "resolved",
    submittedAt: "2026-04-27T16:40:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-21",
    code: "FLG-20462",
    userId: "user-amira",
    name: "Amira Patel",
    city: "Dallas, TX",
    moduleKey: "photography",
    reason: "Inappropriate content in uploads",
    description: "Uploaded set contains content that violates community guidelines.",
    status: "under_review",
    submittedAt: "2026-04-26T12:08:00",
    evidence: [{ id: "ev-5", name: "gallery-shot", size: "1.1 MB", type: "IMG" }],
    messages: [],
  },
  {
    id: "dummy-22",
    code: "FLG-20461",
    userId: "user-olivia",
    name: "Olivia Chen",
    city: "Houston, TX",
    moduleKey: "messages",
    reason: "Harassment or inappropriate behavior",
    description: "Repeated unwanted messages after being asked to stop.",
    status: "withdrawn",
    submittedAt: "2026-04-25T07:50:00",
    evidence: [],
    messages: [],
  },
  {
    id: "dummy-23",
    code: "FLG-20460",
    userId: "user-mark",
    name: "Mark Carlo",
    city: "Dallas, TX",
    moduleKey: "inbox_post",
    reason: "Unauthorized commercial activity",
    description: "Inbox post is advertising an off-platform paid service.",
    status: "closed",
    submittedAt: "2026-04-24T11:15:00",
    evidence: [],
    messages: [],
  },
]

export const FLAG_LIST_INCLUDES = ["flaggable", "media"]

export const MODULE_TO_API = {
  member_profile: "profile",
  inbox_post: "inbox",
  messages: "messages",
  project: "project",
  photography: "photography",
}

const normalizeModuleKey = value => {
  const raw = String(value || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (MODULE_META[raw]) return raw
  if (raw.includes("profile") || raw.includes("member") || raw === "user") {
    return "member_profile"
  }
  if (raw.includes("inbox") || raw.includes("post")) return "inbox_post"
  if (raw.includes("message")) return "messages"
  if (raw.includes("photo")) return "photography"
  if (raw.includes("project")) return "project"
  return "member_profile"
}

const normalizeStatus = value => {
  const raw = String(value || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (
    raw === "pending" ||
    raw === "open" ||
    raw === "opened" ||
    raw === "review"
  ) {
    return "under_review"
  }
  if (STATUS_META[raw]) return raw
  return "under_review"
}

const formatReason = item => {
  if (Array.isArray(item?.reasons) && item.reasons.length > 0) {
    return item.reasons.filter(Boolean).join(", ")
  }
  if (item?.reason || item?.title) return item.reason || item.title
  return "--"
}

const fileNameFromPath = path => {
  if (!path || typeof path !== "string") return "Attachment"
  const parts = path.split("/").filter(Boolean)
  return parts[parts.length - 1] || "Attachment"
}

const mapFlagMedia = (files = []) =>
  files.map((file, index) => {
    const url =
      file.path_url ||
      file.thumbnail_path_url ||
      file.url ||
      file.original_url ||
      file.path
    return {
      id: file.id || `file-${index}`,
      name:
        file.name ||
        file.file_name ||
        file.original_name ||
        fileNameFromPath(file.path || url),
      size: file.size_label || file.size || "",
      type: file.type || file.extension || file.mime_type || "IMG",
      url,
      thumbnail: file.thumbnail_path_url || url,
    }
  })

export const mapApiConversation = (item, currentUserId) => {
  const sender = item.sender || item.user || {}
  const author =
    getUserFullName(sender, {
      fallback: formatDisplayName(item.author) || "Member",
    })
  const isYou = String(item.sender_id || sender.id || "") === String(currentUserId || "")

  return {
    id: item.id,
    author,
    role: isYou ? "You" : item.role || sender.type || "",
    isYou,
    avatar: getUserAvatarUrl(sender),
    createdAt: item.created_at || item.createdAt,
    body: item.message || item.body || item.content || "",
  }
}

export const formatSubmittedDate = value => {
  if (!value) return "--"
  const date = dayjs(value)
  return date.isValid() ? date.format("MMM D, YYYY") : "--"
}

export const formatSubmittedDateTime = value => {
  if (!value) return "--"
  const date = dayjs(value)
  return date.isValid() ? date.format("MMM D, YYYY [at] h:mm A") : "--"
}

export const formatRelativeTime = value => {
  if (!value) return ""
  const date = dayjs(value)
  if (!date.isValid()) return ""
  const minutes = dayjs().diff(date, "minute")
  if (minutes < 1) return "Just now"
  if (minutes < 60) return `${minutes} minutes ago`
  const hours = dayjs().diff(date, "hour")
  if (hours === 1) return "1 hour ago"
  if (hours < 24) return `${hours} hours ago`
  const days = dayjs().diff(date, "day")
  if (days === 1) return "1 day ago"
  return `${days} days ago`
}

export const enrichFlag = flag => {
  const moduleKey = normalizeModuleKey(flag.moduleKey || flag.module)
  const status = normalizeStatus(flag.status)
  const moduleMeta = MODULE_META[moduleKey] || MODULE_META.member_profile
  const statusMeta = STATUS_META[status] || STATUS_META.under_review

  return {
    ...flag,
    moduleKey,
    status,
    moduleLabel: moduleMeta.label,
    moduleTone: moduleMeta.tone,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    tab: statusMeta.tab,
    submittedLabel: formatSubmittedDate(flag.submittedAt),
    submittedDateTime: formatSubmittedDateTime(flag.submittedAt),
    evidence: flag.evidence || [],
    messages: flag.messages || [],
    city: flag.city || "--",
    code: flag.code || `FLG-${flag.id}`,
  }
}

export const mapApiFlag = item => {
  const flaggable =
    (typeof item.flaggable === "object" && item.flaggable) ||
    item.flagged_user ||
    item.user ||
    item.subject ||
    {}
  // Profile/module flags may nest the member under flaggable.user
  const user =
    flaggable?.user && typeof flaggable.user === "object"
      ? flaggable.user
      : flaggable
  const name =
    getUserFullName(user) ||
    getUserFullName(flaggable) ||
    formatDisplayName(item.user_name) ||
    formatDisplayName(item.name) ||
    "Unknown"

  const cityParts = [
    user.city?.city ||
      user.city_name ||
      flaggable.city?.city ||
      item.city?.city,
    user.city?.state ||
      user.city?.country ||
      flaggable.city?.country ||
      item.city?.country,
  ].filter(Boolean)

  const reason = formatReason(item)
  const rawDescription = String(item.description || item.details || "").trim()
  // Additional details live in description. Ignore legacy rows that only
  // duplicated the reason label into description.
  const description =
    rawDescription &&
    rawDescription.toLowerCase() !== String(reason || "").trim().toLowerCase()
      ? rawDescription
      : ""

  const resolvedUserId =
    user.id ||
    user.user_id ||
    flaggable.user_id ||
    item.flaggable_id ||
    item.user_id ||
    item.subject_id

  return enrichFlag({
    id: item.id,
    code: item.flag_code || item.code,
    userId: resolvedUserId,
    name,
    avatar: getUserAvatarUrl(user) || getUserAvatarUrl(flaggable) || item.avatar,
    city:
      cityParts.length > 0
        ? cityParts.join(", ")
        : item.city_name || user.city || "",
    moduleKey:
      item.module ||
      item.module_key ||
      item.flaggable_type ||
      item.subject_type,
    reason,
    description,
    status: item.status,
    submittedAt: item.created_at || item.submitted_at,
    evidence: mapFlagMedia(
      item.media || item.evidence || item.attachments || [],
    ),
    flaggableId: resolvedUserId || item.flaggable_id,
    messages: (item.comments || item.messages || item.thread || []).map(
      message => mapApiConversation(message),
    ),
  })
}

export const isExistingMemberProfileFlag = (flag, userId) => {
  const target = String(userId || "").trim()
  if (!target || !flag) return false
  if (flag.moduleKey !== "member_profile") return false
  if (flag.status === "withdrawn") return false
  return [flag.userId, flag.flaggableId].some(
    id => id && String(id) === target,
  )
}

export const fetchExistingMemberProfileFlag = async userId => {
  if (!userId) return null

  const findOnEndpoint = async url => {
    let page = 1
    let lastPage = 1

    do {
      const response = await get(url, {
        params: {
          page,
          page_size: 50,
          module: MODULE_TO_API.member_profile,
          flaggable_id: userId,
          "with[]": FLAG_LIST_INCLUDES,
        },
        skipErrorToast: true,
      })

      if (!response?.data?.success) return null

      const result = response.data.result || {}
      const list = Array.isArray(result) ? result : result.data || []
      const match = list
        .map(mapApiFlag)
        .find(flag => isExistingMemberProfileFlag(flag, userId))

      if (match) return match

      lastPage = Number(result.last_page) || 1
      page += 1
    } while (page <= lastPage && page <= 5)

    return null
  }

  return (
    (await findOnEndpoint(endpoints.getMyFlags)) ||
    (await findOnEndpoint(endpoints.getFlags))
  )
}

/**
 * Members the current user has flagged (non-withdrawn).
 * Uses GET /api/v1/flags (same shape as the Flags listing).
 * Invite search disables these users but still shows them as "Flagged".
 */
export const fetchMyFlaggedMembers = async () => {
  const ids = new Set()
  const emails = new Set()

  const collectFromItem = item => {
    if (!item || typeof item !== "object") return

    const status = String(item.status || "")
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
    // Backend still blocks invites for resolved flags; only skip withdrawn.
    if (status === "withdrawn") return

    const type = String(item.flaggable_type || item.module || "")
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
    const isUserTarget =
      !type ||
      type.includes("user") ||
      type.includes("profile") ||
      type.includes("member")
    if (!isUserTarget) return

    const flaggable =
      typeof item.flaggable === "object" && item.flaggable
        ? item.flaggable
        : null
    const nestedUser =
      flaggable?.user && typeof flaggable.user === "object"
        ? flaggable.user
        : null

    // Prefer the flagged member id — never the reporter (item.user_id).
    ;[
      nestedUser?.id,
      flaggable?.id,
      item.flaggable_id,
    ]
      .map(value => String(value || "").trim())
      .filter(Boolean)
      .forEach(id => {
        ids.add(id)
        ids.add(id.toLowerCase())
      })

    const email = String(nestedUser?.email || flaggable?.email || "")
      .trim()
      .toLowerCase()
    if (email) emails.add(email)
  }

  const fetchAllPages = async url => {
    let page = 1
    let lastPage = 1

    do {
      let response = null
      try {
        response = await get(url, {
          params: {
            page,
            page_size: 50,
            "with[]": ["flaggable", "media"],
          },
          skipErrorToast: true,
        })
      } catch {
        break
      }

      if (!response?.data?.success) break

      const result = response.data.result || {}
      const list = Array.isArray(result) ? result : result.data || []
      list.forEach(collectFromItem)

      lastPage = Number(result.last_page) || 1
      page += 1
    } while (page <= lastPage && page <= 10)
  }

  // Flags listing endpoint is the source of truth for member invites.
  await fetchAllPages(endpoints.getFlags)
  if (ids.size === 0) {
    await fetchAllPages(endpoints.getMyFlags)
  }

  return {
    userIds: [...ids],
    emails: [...emails],
  }
}

/** @deprecated Prefer fetchMyFlaggedMembers */
export const fetchMyFlaggedMemberUserIds = async () => {
  const { userIds } = await fetchMyFlaggedMembers()
  return userIds
}

export const getDummyFlags = () => DUMMY_FLAGS.map(enrichFlag)

export const findDummyFlag = id =>
  getDummyFlags().find(flag => String(flag.id) === String(id)) || null
